import { Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";
import { useFormContext } from "react-hook-form";

import { CustomButton } from "@/components/Button";
import theme from "@/theme";

import {
  RegistrationContext,
  RegistrationStepContext,
  RegistrationFlow,
  registrationFieldLabels,
} from "./RegistrationContext";
import { getRegistrationStepFields, registrationStepFields } from "./registrationStepFields";

const useStyles = makeStyles(() => ({
  actions: { marginTop: 32, "& > div:not([role=alert])": { maxWidth: 360, marginLeft: "auto", marginRight: "auto" } },
  summary: {
    borderRadius: 8,
    padding: 16,
    backgroundColor: theme.palette.semantic["status/error-bg"],
    color: theme.palette.semantic["text/error"],
    marginBottom: 16,
  },
  errorLink: {
    font: "inherit",
    fontSize: 12,
    color: "inherit",
    background: "none",
    border: 0,
    textDecoration: "underline",
    cursor: "pointer",
    padding: "4px 8px 4px 0",
    textAlign: "left",
  },
  title: { fontSize: 14, fontWeight: 700 },
}));

export const getRegistrationFieldStep = (flow: RegistrationFlow, field: string) => {
  const index = registrationStepFields[flow].findIndex((fields) => (fields as readonly string[]).includes(field));
  return index < 0 ? 0 : index;
};

/** ステップ移動後にも先頭の該当行へフォーカスを合わせる。 */
export const focusRegistrationField = (field: string) => {
  const started = Date.now();
  const focus = () => {
    const label = registrationFieldLabels[field];
    const row = Array.from(document.querySelectorAll<HTMLElement>("[data-registration-field]")).find(
      (item) =>
        (item.dataset.registrationField === field || item.dataset.registrationField === label) &&
        !item.closest("[hidden]")
    );
    if (row) {
      row.scrollIntoView?.({ block: "center", behavior: "smooth" });
      row.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
    } else if (Date.now() - started < 1000) window.setTimeout(focus, 50);
  };
  window.setTimeout(focus, 0);
};

export const RegistrationErrorSummary: React.FC<{ onSelect?: (step: number) => void }> = ({ onSelect }) => {
  const v2 = React.useContext(RegistrationContext);
  const classes = useStyles();
  const methods = useFormContext();
  const { flow, step } = React.useContext(RegistrationStepContext);
  const errors = methods?.formState.errors || {};
  const fields = Object.keys(errors).filter(
    (field) => step === undefined || (getRegistrationStepFields(flow, step) as string[]).includes(field)
  );
  if (!v2 || !fields.length) return null;
  return (
    <Box role="alert" className={classes.summary}>
      <Typography className={classes.title}>{fields.length}項目の入力を確認してください</Typography>
      {fields.map((field) => (
        <button
          key={field}
          type="button"
          className={classes.errorLink}
          onClick={() => {
            if (onSelect) onSelect(getRegistrationFieldStep(flow, field));
            focusRegistrationField(field);
          }}
        >
          {registrationFieldLabels[field] || field}
        </button>
      ))}
    </Box>
  );
};

export const RegistrationActions: React.FC<{ onErrorSelect?: (step: number) => void }> = ({
  children,
  onErrorSelect,
}) => {
  const classes = useStyles();
  return (
    <Box className={classes.actions}>
      <RegistrationErrorSummary onSelect={onErrorSelect} />
      {children}
    </Box>
  );
};

export const RegistrationBack: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Box mt={0.5}>
    <CustomButton
      type="button"
      tone="text"
      buttonSize="L"
      onClick={onClick}
      style={{ color: theme.palette.primary.main }}
    >
      戻る
    </CustomButton>
  </Box>
);

export const RegistrationSection: React.FC<{ title: string }> = ({ title }) => (
  <Typography component="h3" variant="h4" data-registration-section>
    {title}
  </Typography>
);
